/**
 * The service strip and the footer (sections/dm-service-strip.liquid, sections/dm-footer.liquid).
 *
 *   · dm-service-strip: under 750 one note at a time, with chevrons and swipe. The (i) note on
 *     "Dogs in Peru" closes on a second tap, Esc and an outside tap, and never leaves the viewport.
 *   · dm-footer: under 750 plus-minus accordions, one open at a time. Subscribe reveals the email
 *     field, and it reopens by itself after the form posts so the message can be read.
 *
 * FAIL-SAFE BY DESIGN. The CSS only hides things once this script sets data-ready. If it never
 * runs, the notes stack, every footer link stays visible and the email field stays open.
 */

const SWIPE_MIN = 40;
const POP_GUTTER = 16;

const isPhone = () => window.matchMedia('(max-width: 749px)').matches;

class DmServiceStrip extends HTMLElement {
  /** @type {HTMLElement[]} */
  #items = [];
  #index = 0;

  /** @type {{ x: number, y: number } | null} */
  #touch = null;

  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {AbortController | null} */
  #popListeners = null;

  /** @type {HTMLElement | null} */
  #info = null;

  /** @type {HTMLElement | null} */
  #pop = null;

  connectedCallback() {
    this.#items = [...this.querySelectorAll('[data-dm-svc-item]')];
    this.#index = Math.max(
      0,
      this.#items.findIndex((item) => item.classList.contains('is-on'))
    );
    this.#info = this.querySelector('[data-dm-svc-info]');
    this.#pop = this.querySelector('[data-dm-svc-pop]');
    if (this.#pop) this.#pop.hidden = true;

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true, signal });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true, signal });
    this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

    this.dataset.ready = '';
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#popListeners?.abort();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-dm-svc-prev]')) this.#show(this.#index - 1);
    else if (event.target.closest('[data-dm-svc-next]')) this.#show(this.#index + 1);
    else if (event.target.closest('[data-dm-svc-info]')) {
      this.#setPop(this.#info?.getAttribute('aria-expanded') !== 'true');
    }
  };

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    const touch = event.touches[0];
    this.#touch = event.touches.length === 1 && touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  /** @param {TouchEvent} event */
  #onTouchEnd = (event) => {
    const start = this.#touch;
    const touch = event.changedTouches[0];
    this.#touch = null;
    if (!start || !touch || !isPhone()) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy) * 1.5) return;

    this.#show(this.#index + (dx < 0 ? 1 : -1));
  };

  /** The theme editor: show the note the owner just selected. @param {Event} event */
  #onBlockSelect = (event) => {
    const item = event.target instanceof Element ? event.target.closest('[data-dm-svc-item]') : null;
    const index = item instanceof HTMLElement ? this.#items.indexOf(item) : -1;
    if (index >= 0) this.#show(index);
  };

  /** @param {number} index */
  #show(index) {
    const count = this.#items.length;
    if (!count) return;

    this.#setPop(false);
    this.#items[this.#index]?.classList.remove('is-on');
    this.#index = (index + count) % count;
    this.#items[this.#index]?.classList.add('is-on');
  }

  /** @param {boolean} open */
  #setPop(open) {
    const pop = this.#pop;
    const info = this.#info;
    if (!pop || !info) return;

    pop.hidden = !open;
    info.setAttribute('aria-expanded', String(open));
    this.#popListeners?.abort();
    this.#popListeners = null;
    if (!open) return;

    this.#placePop();

    this.#popListeners = new AbortController();
    const { signal } = this.#popListeners;
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (event.target instanceof Node && (pop.contains(event.target) || info.contains(event.target))) return;
        this.#setPop(false);
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        this.#setPop(false);
        info.focus();
      },
      { signal }
    );
    window.addEventListener('resize', this.#placePop, { passive: true, signal });
  }

  /** Keeps the note inside the viewport: sideways first, then below the row if the top is cut. */
  #placePop = () => {
    const pop = this.#pop;
    if (!pop) return;

    pop.style.removeProperty('--dm-pop-shift');
    delete pop.dataset.flip;

    const box = pop.getBoundingClientRect();
    const view = document.documentElement.clientWidth;
    let shift = 0;
    if (box.right > view - POP_GUTTER) shift = view - POP_GUTTER - box.right;
    if (box.left + shift < POP_GUTTER) shift = POP_GUTTER - box.left;
    if (shift) pop.style.setProperty('--dm-pop-shift', `${Math.round(shift)}px`);
    if (box.top < POP_GUTTER) pop.dataset.flip = '';
  };
}

class DmFooter extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });

    // After the form posts the page reloads with its message, so keep the message in view.
    if (this.querySelector('[data-dm-footer-msg]')) this.#openNews(false);

    this.dataset.ready = '';
  }

  disconnectedCallback() {
    this.#listeners?.abort();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-dm-footer-toggle]');
    if (toggle) this.#toggle(toggle);
    else if (event.target.closest('[data-dm-footer-sub]')) this.#openNews(true);
  };

  /** Ship to, only when the store sells to more than one country. @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-dm-footer-country]')) return;
    select.form?.submit();
  };

  /** @param {Element} button */
  #toggle(button) {
    const open = button.getAttribute('aria-expanded') === 'true';

    for (const toggle of this.querySelectorAll('[data-dm-footer-toggle]')) {
      const on = toggle === button && !open;
      toggle.setAttribute('aria-expanded', String(on));
      document.getElementById(toggle.getAttribute('aria-controls') ?? '')?.classList.toggle('is-open', on);
    }
  }

  /** @param {boolean} focus */
  #openNews(focus) {
    const sub = this.querySelector('[data-dm-footer-sub]');
    this.querySelector('.dm-footer__form')?.classList.add('is-open');

    if (sub instanceof HTMLElement) {
      sub.setAttribute('aria-expanded', 'true');
      sub.hidden = true;
    }

    if (focus) {
      const email = this.querySelector('[data-dm-footer-email]');
      if (email instanceof HTMLElement) email.focus();
    }
  }
}

if (!customElements.get('dm-service-strip')) customElements.define('dm-service-strip', DmServiceStrip);
if (!customElements.get('dm-footer')) customElements.define('dm-footer', DmFooter);
